import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  appApi,
  useGetSsConfigQuery,
  useGetSsProfilesQuery,
  useGetSsRunsQuery,
  useGetSsProjectsQuery,
  useGetSsCompaniesQuery,
  useGetSsRunQuery,
} from "../../../store/appApi";
import {
  createSsCompanyApi,
  updateSsCompanyApi,
  updateSsConfigApi,
  upsertSsProfileApi,
  generateSsRunApi,
  updateSsRowApi,
  setSsRowPaidApi,
  finaliseSsRunApi,
  deleteSsRunApi,
  downloadSsRunExcel,
} from "../../../services/salarySheet";

const panel = "rounded-2xl border border-gray-200 bg-white shadow-sm";
const field =
  "rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20";
const primary =
  "rounded-lg bg-cyan-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-50";
const ghost =
  "rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-cyan-400 hover:text-cyan-700 disabled:opacity-50";

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";
const thisMonth = () => new Date().toISOString().slice(0, 7);

const Kpi = ({ label, value, tone }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-center">
    <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className={`mt-0.5 text-sm font-semibold tabular-nums ${tone || "text-gray-900"}`}>
      {value}
    </div>
  </div>
);

// ── numeric cell that PATCHes on blur ──────────────────────────────────────
const NumCell = ({ value, disabled, onSave }) => {
  const [v, setV] = useState(String(value ?? 0));
  useEffect(() => setV(String(value ?? 0)), [value]);
  return (
    <input
      type="number"
      className={`${field} w-24 text-right`}
      value={v}
      disabled={disabled}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (String(value ?? 0) !== v && v !== "") onSave(Number(v));
      }}
    />
  );
};

// ═══════════════════════════════ Profiles tab ═══════════════════════════════
const ProfilesTab = () => {
  const dispatch = useDispatch();
  const { data, isFetching } = useGetSsProfilesQuery();
  const serverRows = data?.profiles || [];
  // Extra "split" shares added locally, saved as new profiles.
  const [extras, setExtras] = useState([]);
  const rk = (p) => p.profileId || p.key || p.employeeId;
  const lastIdx = new Map();
  serverRows.forEach((p, i) => lastIdx.set(String(p.employeeId), i));
  const profiles = serverRows.flatMap((p, i) => [
    p,
    ...(lastIdx.get(String(p.employeeId)) === i
      ? extras.filter((x) => String(x.employeeId) === String(p.employeeId))
      : []),
  ]);
  const { data: coData } = useGetSsCompaniesQuery();
  const companies = (coData?.companies || []).filter((c) => c.active !== false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");

  const edit = (id, patch) =>
    setDraft((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const addSplit = (p) =>
    setExtras((x) => [
      ...x,
      {
        key: `new-${Date.now()}`,
        employeeId: p.employeeId,
        name: p.name,
        role: p.role,
        isNew: true,
        split: true,
        salaryCompanyId: null,
        grossSalary: 0,
        bankName: "",
        bankAccount: "",
        bankIfsc: "",
        bankBranch: "",
        conveyance: 0,
        phoneExpense: 0,
      },
    ]);

  const save = async (p) => {
    const body = draft[rk(p)];
    if (!body) return;
    if (p.isNew && !(body.salaryCompanyId || "")) {
      Swal.fire({ icon: "info", title: "Choose the company for this share." });
      return;
    }
    try {
      setBusy(rk(p));
      await upsertSsProfileApi(p.employeeId, {
        ...body,
        profileId: p.profileId || undefined,
      });
      dispatch(appApi.util.invalidateTags(["SalarySheet"]));
      if (p.isNew) setExtras((x) => x.filter((r) => r.key !== p.key));
      setDraft((d) => {
        const n = { ...d };
        delete n[rk(p)];
        return n;
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Save failed", text: errMsg(e) });
    } finally {
      setBusy("");
    }
  };

  const val = (p, k) => draft[rk(p)]?.[k] ?? p[k] ?? "";

  return (
    <div className={`${panel} overflow-x-auto`}>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Employee</th>
            <th className="px-3 py-2 text-left">Company</th>
            <th className="px-3 py-2 text-right">Gross</th>
            <th className="px-3 py-2 text-left">Bank name</th>
            <th className="px-3 py-2 text-left">Account</th>
            <th className="px-3 py-2 text-left">IFSC</th>
            <th className="px-3 py-2 text-left">Branch</th>
            <th className="px-3 py-2 text-right">Conveyance</th>
            <th className="px-3 py-2 text-right">Phone exp.</th>
            <th className="px-3 py-2 text-right" title="Gross + conveyance + phone. Excludes the absence deduction and any advance, which are worked out per monthly sheet.">
              Est. net
            </th>
            <th className="px-3 py-2 text-right">Save</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isFetching && profiles.length === 0 ? (
            <tr>
              <td colSpan={11} className="px-3 py-10 text-center text-gray-400">
                Loading…
              </td>
            </tr>
          ) : (
            profiles.map((p) => {
              const dirty = !!draft[rk(p)];
              return (
                <tr key={rk(p)} className={p.isNew ? "bg-violet-50/40" : "hover:bg-cyan-50/30"}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{p.name}</div>
                    <div className="text-[11px] text-gray-400">
                      {p.role}
                      {p.split && (
                        <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">
                          split salary
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className={`${field} w-36`}
                      value={val(p, "salaryCompanyId") || ""}
                      onChange={(e) =>
                        edit(rk(p), { salaryCompanyId: e.target.value })
                      }
                    >
                      <option value="">— none —</option>
                      {companies.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      className={`${field} w-28 text-right`}
                      value={val(p, "grossSalary")}
                      onChange={(e) =>
                        edit(rk(p), { grossSalary: e.target.value })
                      }
                    />
                  </td>
                  {["bankName", "bankAccount", "bankIfsc", "bankBranch"].map((k) => (
                    <td key={k} className="px-3 py-2">
                      <input
                        className={`${field} w-32`}
                        value={val(p, k)}
                        onChange={(e) =>
                          edit(rk(p), { [k]: e.target.value })
                        }
                      />
                    </td>
                  ))}
                  {["conveyance", "phoneExpense"].map((k) => (
                    <td key={k} className="px-3 py-2 text-right">
                      <input
                        type="number"
                        className={`${field} w-24 text-right`}
                        value={val(p, k)}
                        onChange={(e) =>
                          edit(rk(p), { [k]: e.target.value })
                        }
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-800">
                    {inr(
                      Number(val(p, "grossSalary") || 0) +
                        Number(val(p, "conveyance") || 0) +
                        Number(val(p, "phoneExpense") || 0),
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className={`${ghost} mr-1`}
                      title="Add another company's share of this employee's salary"
                      onClick={() => addSplit(p)}
                    >
                      + Split
                    </button>
                    <button
                      className={primary}
                      disabled={!dirty || busy === rk(p)}
                      onClick={() => save(p)}
                    >
                      {busy === rk(p) ? "…" : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

// ═══════════════════════════════ Companies tab ═════════════════════════════
const CompaniesTab = () => {
  const dispatch = useDispatch();
  const { data } = useGetSsCompaniesQuery();
  const companies = data?.companies || [];
  const [form, setForm] = useState({ name: "", code: "", city: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => dispatch(appApi.util.invalidateTags(["SalarySheet"]));

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      setBusy(true);
      await createSsCompanyApi(form);
      setForm({ name: "", code: "", city: "" });
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not add", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const rename = async (c) => {
    const { value } = await Swal.fire({
      title: "Rename company",
      input: "text",
      inputValue: c.name,
      showCancelButton: true,
    });
    if (!value || value.trim() === c.name) return;
    try {
      await updateSsCompanyApi(c._id, { name: value });
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    }
  };

  const toggle = async (c) => {
    try {
      await updateSsCompanyApi(c._id, { active: c.active === false });
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <div className={`${panel} p-4`}>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Add company
        </div>
        {[
          ["name", "Company name"],
          ["code", "Code (optional)"],
          ["city", "City (optional)"],
        ].map(([k, ph]) => (
          <input
            key={k}
            className={`${field} mt-2 w-full`}
            placeholder={ph}
            value={form[k]}
            onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
          />
        ))}
        <button className={`${primary} mt-3 w-full`} disabled={busy} onClick={add}>
          {busy ? "Adding…" : "Add company"}
        </button>
        <p className="mt-2 text-[11px] text-gray-400">
          Companies are used only here. Assign one to each employee on the
          Employee profiles tab, then generate a company-wise sheet.
        </p>
      </div>
      <div className={`${panel} overflow-x-auto`}>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Company</th>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">City</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {companies.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-gray-400">
                  No companies yet.
                </td>
              </tr>
            )}
            {companies.map((c) => (
              <tr key={c._id}>
                <td className="px-3 py-2 font-medium text-gray-800">{c.name}</td>
                <td className="px-3 py-2 text-gray-500">{c.code || "—"}</td>
                <td className="px-3 py-2 text-gray-500">{c.city || "—"}</td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                      c.active === false
                        ? "bg-gray-100 text-gray-500"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {c.active === false ? "inactive" : "active"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button className={ghost} onClick={() => rename(c)}>
                    Rename
                  </button>{" "}
                  <button className={ghost} onClick={() => toggle(c)}>
                    {c.active === false ? "Activate" : "Deactivate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════ Settings tab ══════════════════════════════
const SettingsTab = () => {
  const dispatch = useDispatch();
  const { data } = useGetSsConfigQuery();
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (data?.config) setForm(data.config);
  }, [data]);
  if (!form) return <div className="text-sm text-gray-400">Loading…</div>;

  const save = async () => {
    try {
      await updateSsConfigApi(form);
      dispatch(appApi.util.invalidateTags(["SalarySheet"]));
      Swal.fire({ icon: "success", title: "Saved", timer: 1400, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    }
  };
  const f = (k, label, hint) => (
    <label className="block">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <input
        type="number"
        className={`${field} mt-1 w-full`}
        value={form[k] ?? 0}
        onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
      />
      {hint && <span className="mt-0.5 block text-[11px] text-gray-400">{hint}</span>}
    </label>
  );

  return (
    <div className={`${panel} max-w-md space-y-4 p-5`}>
      {f("leaveAllowance", "Free leaves per month", "Paid leave days before salary is docked")}
      {f("perDayDivisor", "Per-day divisor", "Per-day rate = gross ÷ this (default 30)")}
      {f("defaultConveyance", "Default conveyance", "Seeded onto new profiles")}
      {f("defaultPhoneExpense", "Default phone expense", "Seeded onto new profiles")}
      <button className={primary} onClick={save}>
        Save settings
      </button>
    </div>
  );
};

// ═══════════════════════════════ Run detail ════════════════════════════════
const RunDetail = ({ runId, onDeleted, advanceMode, onAdvanceDone }) => {
  const dispatch = useDispatch();
  const { data, isFetching } = useGetSsRunQuery({ id: runId }, { skip: !runId });
  const run = data?.run;
  const rows = data?.rows || [];
  const [busy, setBusy] = useState(false);
  const locked = run?.status === "final";

  const patchRow = async (rowId, body) => {
    try {
      await updateSsRowApi(runId, rowId, body);
      dispatch(appApi.util.invalidateTags([{ type: "SalarySheet", id: runId }]));
    } catch (e) {
      Swal.fire({ icon: "error", title: "Update failed", text: errMsg(e) });
    }
  };
  const togglePaid = async (row) => {
    try {
      await setSsRowPaidApi(runId, row._id, { paid: !row.paid });
      dispatch(appApi.util.invalidateTags([{ type: "SalarySheet", id: runId }, "SalarySheet"]));
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    }
  };
  const finalise = async (reopen) => {
    try {
      setBusy(true);
      await finaliseSsRunApi(runId, reopen);
      dispatch(appApi.util.invalidateTags([{ type: "SalarySheet", id: runId }, "SalarySheet"]));
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const ok = await Swal.fire({
      icon: "warning",
      title: `Delete the ${run?.monthKey} sheet?`,
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;
    await deleteSsRunApi(runId);
    dispatch(appApi.util.invalidateTags(["SalarySheet"]));
    onDeleted?.();
  };

  if (!runId) return null;
  if (isFetching && !run)
    return <div className="text-sm text-gray-400">Loading…</div>;
  if (!run) return null;

  const t = run.totals || {};
  return (
    <div className={`${panel} min-w-0`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div>
          <span className="text-sm font-semibold text-gray-800">
            Salary sheet · {run.monthKey}
          </span>
          <span
            className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
              locked
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {run.status}
          </span>
          <span className="ml-2 text-[11px] text-gray-400">
            {run.paidCount || 0}/{run.employeeCount || rows.length} paid · holidays{" "}
            {run.holidayDays} · rate ÷{run.perDayDivisor} · free {run.leaveAllowance}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            className={ghost}
            onClick={() => downloadSsRunExcel(runId, run.monthKey)}
          >
            Download Excel
          </button>
          <button className={ghost} disabled={busy} onClick={() => finalise(locked)}>
            {locked ? "Reopen" : "Finalise"}
          </button>
          <button
            className={`${ghost} hover:border-rose-400 hover:text-rose-600`}
            onClick={remove}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 p-3 sm:grid-cols-6">
        <Kpi label="Gross" value={inr(t.gross)} />
        <Kpi label="− Advance" value={inr(t.advance)} tone="text-rose-600" />
        <Kpi label="− Absent" value={inr(t.absent)} tone="text-rose-600" />
        <Kpi label="+ Conv./Phone" value={inr((t.conveyance || 0) + (t.phone || 0))} tone="text-emerald-600" />
        <Kpi label="Net payable" value={inr(t.net)} tone="text-gray-900" />
        <Kpi label="Paid" value={inr(t.paid)} tone="text-emerald-600" />
      </div>

      <div className="max-h-[70vh] max-w-full overflow-auto">
        {advanceMode && !locked && (
          <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            <span>
              Enter each employee&apos;s advance deduction in the highlighted
              <b> − Advance</b> column — every change saves automatically.
            </span>
            <button className={ghost} onClick={onAdvanceDone}>
              Done
            </button>
          </div>
        )}
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Bank</th>
              <th className="px-3 py-2 text-right">Gross</th>
              <th className={`px-3 py-2 text-right ${advanceMode && !locked ? "bg-amber-100 text-amber-800" : ""}`}>− Advance</th>
              <th className="px-3 py-2 text-center">Hol.</th>
              <th className="px-3 py-2 text-center">Absent</th>
              <th className="px-3 py-2 text-center">Excess</th>
              <th className="px-3 py-2 text-right">− Absent ₹</th>
              <th className="px-3 py-2 text-right">+ Conv.</th>
              <th className="px-3 py-2 text-right">+ Phone</th>
              <th className="px-3 py-2 text-right">± Adj.</th>
              <th className="px-3 py-2 text-right">Net</th>
              <th className="px-3 py-2 text-center">Paid</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r._id} className={r.paid ? "bg-emerald-50/50" : "hover:bg-cyan-50/30"}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{r.employeeName}</div>
                  <div className="text-[11px] text-gray-400">{r.role}</div>
                  {r.companyName && (
                    <div className="mt-0.5 inline-block rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                      {r.companyName}
                      {r.split ? " · split" : ""}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-500">
                  <div>{r.bankName || "—"}</div>
                  <div>{r.bankAccount}</div>
                  <div>{r.bankIfsc}{r.bankBranch ? ` · ${r.bankBranch}` : ""}</div>
                </td>
                <td className="px-3 py-2 text-right">
                  <NumCell
                    value={r.grossSalary}
                    disabled={locked}
                    onSave={(v) => patchRow(r._id, { grossSalary: v })}
                  />
                </td>
                <td className={`px-3 py-2 text-right ${advanceMode && !locked ? "bg-amber-50" : ""}`}>
                  <NumCell
                    value={r.advanceDeduction}
                    disabled={locked}
                    onSave={(v) => patchRow(r._id, { advanceDeduction: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center text-gray-500">{r.holidayDays}</td>
                <td className="px-3 py-2 text-center text-gray-700">{r.absentDays}</td>
                <td
                  className={`px-3 py-2 text-center font-semibold ${
                    r.excessDays > 0 ? "text-rose-600" : "text-gray-400"
                  }`}
                >
                  {r.excessDays}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-600">
                  {inr(r.absentAmount)}
                </td>
                <td className="px-3 py-2 text-right">
                  <NumCell
                    value={r.conveyance}
                    disabled={locked}
                    onSave={(v) => patchRow(r._id, { conveyance: v })}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <NumCell
                    value={r.phoneExpense}
                    disabled={locked}
                    onSave={(v) => patchRow(r._id, { phoneExpense: v })}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <NumCell
                    value={r.adjustmentAmount}
                    disabled={locked}
                    onSave={(v) => patchRow(r._id, { adjustmentAmount: v })}
                  />
                </td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {inr(r.netSalary)}
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-emerald-600"
                    checked={!!r.paid}
                    onChange={() => togglePaid(r)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════ Sheets tab ════════════════════════════════
const SheetsTab = () => {
  const dispatch = useDispatch();
  // "" = the active header project (default), "all" = every employee,
  // otherwise a project-wise sheet covering only staff on that project.
  const [projectId, setProjectId] = useState("");
  // "" = no company filter; otherwise a company-wise sheet.
  const [companyId, setCompanyId] = useState("");
  const { data } = useGetSsRunsQuery({ projectId, companyId });
  const { data: coData } = useGetSsCompaniesQuery();
  const companies = (coData?.companies || []).filter((c) => c.active !== false);
  const runs = data?.runs || [];
  const { data: projectData } = useGetSsProjectsQuery();
  const projects = projectData?.projects || [];
  const [month, setMonth] = useState(thisMonth());
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  // After generating: highlight the advance column so deductions can be keyed in.
  const [advanceMode, setAdvanceMode] = useState(false);

  const openRunId = openId || runs[0]?._id || null;

  const generate = async () => {
    try {
      setBusy(true);
      const res = await generateSsRunApi({
        month,
        projectId: projectId || undefined,
        companyId: companyId || undefined,
      });
      dispatch(appApi.util.invalidateTags(["SalarySheet"]));
      setOpenId(res.data?.run?._id || null);
      const ask = await Swal.fire({
        icon: "question",
        title: res.data?.message || "Sheet generated",
        text: "Any employee advance deductions this month?",
        showDenyButton: true,
        confirmButtonText: "Yes, enter advances",
        denyButtonText: "No",
      });
      setAdvanceMode(ask.isConfirmed);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Generate failed", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
      <div className={`${panel} p-4`}>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Generate a month
        </div>
        <select
          className={`${field} mt-2 w-full`}
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
        >
          <option value="">Active project (default)</option>
          <option value="all">All employees (no project filter)</option>
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.projectName}
            </option>
          ))}
        </select>
        <select
          className={`${field} mt-2 w-full`}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        >
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          type="month"
          className={`${field} mt-2 w-full`}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        <button className={`${primary} mt-2 w-full`} disabled={busy} onClick={generate}>
          {busy ? "Generating…" : "Generate / refresh"}
        </button>
        <p className="mt-1 text-[11px] text-gray-400">
          Re-running keeps your manual edits and only refreshes attendance &amp;
          holiday figures.
        </p>

        <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Past sheets
        </div>
        <div className="mt-1 space-y-1">
          {runs.length === 0 && (
            <div className="text-xs text-gray-400">None yet.</div>
          )}
          {runs.map((r) => (
            <button
              key={r._id}
              onClick={() => setOpenId(r._id)}
              className={`flex w-full items-center justify-between rounded-lg border px-3 py-1.5 text-xs ${
                openRunId === r._id
                  ? "border-cyan-400 bg-cyan-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="font-medium">
                {r.monthKey}
                {r.projectId?.projectName && (
                  <span className="ml-1 font-normal text-gray-500">
                    · {r.projectId.projectName}
                  </span>
                )}
                {r.companyId?.name && (
                  <span className="ml-1 font-normal text-violet-600">
                    · {r.companyId.name}
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                {inr(r.totals?.net)}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                    r.status === "final"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {r.status}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <RunDetail
        runId={openRunId}
        advanceMode={advanceMode}
        onAdvanceDone={() => setAdvanceMode(false)}
        onDeleted={() => setOpenId(null)}
      />
    </div>
  );
};

// ═══════════════════════════════ Page ══════════════════════════════════════
const TABS = [
  ["sheets", "Monthly sheets"],
  ["profiles", "Employee profiles"],
  ["companies", "Companies"],
  ["settings", "Settings"],
];

const SalarySheet = () => {
  const role = useSelector(selectUserRole);
  const [tab, setTab] = useState("sheets");

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Salary Sheet" motherMenu="Workforce" />
        <div className={`${panel} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
          <p className="mt-1 text-sm text-gray-500">
            The salary sheet is restricted to super-admin accounts.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Salary Sheet" motherMenu="Workforce" />
      <div className="mb-4 inline-flex rounded-full bg-gray-100 p-1">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              tab === k ? "bg-white text-cyan-700 shadow" : "text-gray-500"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "sheets" && <SheetsTab />}
      {tab === "profiles" && <ProfilesTab />}
      {tab === "companies" && <CompaniesTab />}
      {tab === "settings" && <SettingsTab />}
    </>
  );
};

export default SalarySheet;
